import React, {useState, useEffect} from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  IconButton,
  Badge,
  Chip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  PushPin as PushIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as ReadIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {
  fetchNotifications,
  fetchNotification,
  createNotification,
  updateNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  getUnreadCount,
  createWaterQualityAlert,
  createMortalityAlert,
  createFeedStockAlert,
  createMaintenanceReminder,
  type Notification,
  type CreateNotificationDto,
  type NotificationQueryDto,
} from "../../api/notification";
import {fetchPonds, Pond} from "../../api/pond";
import {fetchFeeds, Feed} from "../../api/feed";
import {fetchAnimalGroups, AnimalGroup} from "../../api/animal";

interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({open, onClose}) => {
  const {t} = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // Dropdown data states
  const [ponds, setPonds] = useState<Pond[]>([]);
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [animalGroups, setAnimalGroups] = useState<AnimalGroup[]>([]);

  // Form states for creating notifications
  const [createForm, setCreateForm] = useState<CreateNotificationDto>({
    title: "",
    message: "",
    type: "email",
    priority: "medium",
  });

  // Template form states
  const [templateForm, setTemplateForm] = useState({
    pondId: "",
    pondName: "",
    issue: "",
    animalGroupId: "",
    species: "",
    mortalityRate: 0,
    feedId: "",
    feedName: "",
    currentStock: 0,
    threshold: 0,
    maintenanceType: "",
    dueDate: "",
  });

  useEffect(() => {
    if (open) {
      loadNotifications();
      loadUnreadCount();
    }
  }, [open]);

  // Load dropdown data when template dialog opens
  useEffect(() => {
    if (templateDialogOpen) {
      loadDropdownData();
    }
  }, [templateDialogOpen]);

  const loadDropdownData = async () => {
    try {
      const [pondsData, feedsData, animalGroupsData] = await Promise.all([fetchPonds(), fetchFeeds(), fetchAnimalGroups()]);
      setPonds(pondsData);
      setFeeds(feedsData);
      setAnimalGroups(animalGroupsData);
    } catch (err) {
      console.error("Error loading dropdown data:", err);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await fetchNotifications({limit: 50});
      setNotifications(response.notifications);
    } catch (err) {
      setError("Failed to load notifications");
      console.error("Error loading notifications:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await getUnreadCount();
      setUnreadCount(response.count);
    } catch (err) {
      console.error("Error loading unread count:", err);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications((prev) => prev.map((n) => (n.id === notificationId ? {...n, status: "read", readAt: new Date().toISOString()} : n)));
      loadUnreadCount();
    } catch (err) {
      setError("Failed to mark notification as read");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({...n, status: "read", readAt: new Date().toISOString()})));
      setUnreadCount(0);
    } catch (err) {
      setError("Failed to mark all notifications as read");
    }
  };

  const handleDelete = async (notificationId: string) => {
    if (!notificationId) {
      setError("Invalid notification ID");
      return;
    }
    try {
      await deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      loadUnreadCount();
    } catch (err) {
      setError("Failed to delete notification");
    }
  };

  const handleCreateNotification = async () => {
    try {
      await createNotification(createForm);
      setCreateDialogOpen(false);
      setCreateForm({title: "", message: "", type: "email", priority: "medium"});
      loadNotifications();
    } catch (err) {
      setError("Failed to create notification");
    }
  };

  const handleCreateTemplateNotification = async () => {
    try {
      switch (selectedTemplate) {
        case "water-quality":
          await createWaterQualityAlert({
            pondId: templateForm.pondId,
            pondName: templateForm.pondName,
            issue: templateForm.issue,
          });
          break;
        case "mortality":
          await createMortalityAlert({
            animalGroupId: templateForm.animalGroupId,
            species: templateForm.species,
            mortalityRate: templateForm.mortalityRate,
          });
          break;
        case "feed-stock":
          await createFeedStockAlert({
            feedId: templateForm.feedId,
            feedName: templateForm.feedName,
            currentStock: templateForm.currentStock,
            threshold: templateForm.threshold,
          });
          break;
        case "maintenance":
          await createMaintenanceReminder({
            pondId: templateForm.pondId,
            pondName: templateForm.pondName,
            maintenanceType: templateForm.maintenanceType,
            dueDate: templateForm.dueDate,
          });
          break;
      }
      setTemplateDialogOpen(false);
      setSelectedTemplate("");
      setTemplateForm({
        pondId: "",
        pondName: "",
        issue: "",
        animalGroupId: "",
        species: "",
        mortalityRate: 0,
        feedId: "",
        feedName: "",
        currentStock: 0,
        threshold: 0,
        maintenanceType: "",
        dueDate: "",
      });
      loadNotifications();
    } catch (err) {
      setError("Failed to create template notification");
    }
  };

  const getNotificationIcon = (type: string, priority: string) => {
    const color = priority === "urgent" ? "error" : priority === "high" ? "warning" : priority === "medium" ? "info" : "default";

    switch (type) {
      case "email":
        return <EmailIcon color={color as any} />;
      case "sms":
        return <SmsIcon color={color as any} />;
      case "push":
        return <PushIcon color={color as any} />;
      default:
        return <NotificationsIcon color={color as any} />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "sent":
        return "success";
      case "failed":
        return "error";
      case "pending":
        return "warning";
      case "read":
        return "default";
      default:
        return "default";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {width: 400, maxWidth: "90vw", zIndex: 1401},
        }}>
        <Box sx={{p: 2}}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" component="div">
              {t("Notifications")} {unreadCount > 0 && `(${unreadCount})`}
            </Typography>
            <Box>
              <Tooltip title={t("Create Template Notification")}>
                <IconButton onClick={() => setTemplateDialogOpen(true)} size="small">
                  <AddIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {unreadCount > 0 && (
            <Button variant="outlined" size="small" onClick={handleMarkAllAsRead} sx={{mb: 2}}>
              {t("Mark All as Read")}
            </Button>
          )}

          {error && (
            <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {notifications.length === 0 ? (
                <ListItem>
                  <ListItemText primary={t("No notifications")} secondary={t("You have no notifications at this time")} />
                </ListItem>
              ) : (
                notifications.map((notification) => (
                  <React.Fragment key={notification.id}>
                    <ListItem
                      sx={{
                        backgroundColor: notification.status === "read" ? "transparent" : "action.hover",
                        "&:hover": {backgroundColor: "action.selected"},
                      }}>
                      <ListItemIcon>{getNotificationIcon(notification.type, notification.priority)}</ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle2" component="span">
                              {notification.title}
                            </Typography>
                            <Chip label={notification.priority} size="small" color={getPriorityColor(notification.priority) as any} />
                            <Chip label={notification.status} size="small" color={getStatusColor(notification.status) as any} />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" component="div">
                              {notification.message}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" component="div">
                              {formatDate(notification.createdAt)}
                            </Typography>
                          </Box>
                        }
                        secondaryTypographyProps={{component: "div"}}
                      />
                      <Box>
                        {notification.status === "sent" && (
                          <Tooltip title={t("Mark as Read")}>
                            <IconButton size="small" onClick={() => handleMarkAsRead(notification.id)}>
                              <ReadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t("Delete")}>
                          <IconButton size="small" color="error" onClick={() => handleDelete(notification.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))
              )}
            </List>
          )}
        </Box>
      </Drawer>

      {/* Create Notification Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("Create Notification")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t("Title")}
            value={createForm.title}
            onChange={(e) => setCreateForm({...createForm, title: e.target.value})}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t("Message")}
            value={createForm.message}
            onChange={(e) => setCreateForm({...createForm, message: e.target.value})}
            margin="normal"
            multiline
            rows={3}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>{t("Type")}</InputLabel>
            <Select value={createForm.type} onChange={(e) => setCreateForm({...createForm, type: e.target.value as any})}>
              <MenuItem value="email">{t("Email")}</MenuItem>
              <MenuItem value="sms">{t("SMS")}</MenuItem>
              <MenuItem value="push">{t("Push")}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>{t("Priority")}</InputLabel>
            <Select value={createForm.priority} onChange={(e) => setCreateForm({...createForm, priority: e.target.value as any})}>
              <MenuItem value="low">{t("Low")}</MenuItem>
              <MenuItem value="medium">{t("Medium")}</MenuItem>
              <MenuItem value="high">{t("High")}</MenuItem>
              <MenuItem value="urgent">{t("Urgent")}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t("Cancel")}</Button>
          <Button onClick={handleCreateNotification} variant="contained">
            {t("Create")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Notification Dialog */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("Create Template Notification")}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>{t("Template Type")}</InputLabel>
            <Select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)}>
              <MenuItem value="water-quality">{t("Water Quality Alert")}</MenuItem>
              <MenuItem value="mortality">{t("Mortality Alert")}</MenuItem>
              <MenuItem value="feed-stock">{t("Feed Stock Alert")}</MenuItem>
              <MenuItem value="maintenance">{t("Maintenance Reminder")}</MenuItem>
            </Select>
          </FormControl>

          {selectedTemplate === "water-quality" && (
            <>
              <FormControl fullWidth margin="normal">
                <InputLabel>{t("Pond")}</InputLabel>
                <Select
                  value={templateForm.pondId}
                  onChange={(e) => {
                    const selectedPond = ponds.find((p) => p.id.toString() === e.target.value);
                    setTemplateForm({
                      ...templateForm,
                      pondId: e.target.value,
                      pondName: selectedPond?.name || "",
                    });
                  }}>
                  <MenuItem value="">
                    <em>{t("Select a pond...")}</em>
                  </MenuItem>
                  {ponds.map((pond) => (
                    <MenuItem key={pond.id} value={pond.id.toString()}>
                      {pond.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={t("Issue")}
                value={templateForm.issue}
                onChange={(e) => setTemplateForm({...templateForm, issue: e.target.value})}
                margin="normal"
                multiline
                rows={2}
              />
            </>
          )}

          {selectedTemplate === "mortality" && (
            <>
              <FormControl fullWidth margin="normal">
                <InputLabel>{t("Animal Group")}</InputLabel>
                <Select
                  value={templateForm.animalGroupId}
                  onChange={(e) => {
                    const selectedGroup = animalGroups.find((g) => g.id.toString() === e.target.value);
                    setTemplateForm({
                      ...templateForm,
                      animalGroupId: e.target.value,
                      species: selectedGroup?.species || "",
                    });
                  }}>
                  <MenuItem value="">
                    <em>{t("Select an animal group...")}</em>
                  </MenuItem>
                  {animalGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id.toString()}>
                      {group.name} ({group.species})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={t("Species")}
                value={templateForm.species}
                onChange={(e) => setTemplateForm({...templateForm, species: e.target.value})}
                margin="normal"
              />
              <TextField
                fullWidth
                label={t("Mortality Rate (%)")}
                type="number"
                value={templateForm.mortalityRate}
                onChange={(e) => setTemplateForm({...templateForm, mortalityRate: parseFloat(e.target.value)})}
                margin="normal"
              />
            </>
          )}

          {selectedTemplate === "feed-stock" && (
            <>
              <FormControl fullWidth margin="normal">
                <InputLabel>{t("Feed")}</InputLabel>
                <Select
                  value={templateForm.feedId}
                  onChange={(e) => {
                    const selectedFeed = feeds.find((f) => f.id.toString() === e.target.value);
                    setTemplateForm({
                      ...templateForm,
                      feedId: e.target.value,
                      feedName: selectedFeed?.name || "",
                    });
                  }}>
                  <MenuItem value="">
                    <em>{t("Select a feed...")}</em>
                  </MenuItem>
                  {feeds.map((feed) => (
                    <MenuItem key={feed.id} value={feed.id.toString()}>
                      {feed.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={t("Current Stock (kg)")}
                type="number"
                value={templateForm.currentStock}
                onChange={(e) => setTemplateForm({...templateForm, currentStock: parseFloat(e.target.value)})}
                margin="normal"
              />
              <TextField
                fullWidth
                label={t("Threshold (kg)")}
                type="number"
                value={templateForm.threshold}
                onChange={(e) => setTemplateForm({...templateForm, threshold: parseFloat(e.target.value)})}
                margin="normal"
              />
            </>
          )}

          {selectedTemplate === "maintenance" && (
            <>
              <FormControl fullWidth margin="normal">
                <InputLabel>{t("Pond")}</InputLabel>
                <Select
                  value={templateForm.pondId}
                  onChange={(e) => {
                    const selectedPond = ponds.find((p) => p.id.toString() === e.target.value);
                    setTemplateForm({
                      ...templateForm,
                      pondId: e.target.value,
                      pondName: selectedPond?.name || "",
                    });
                  }}>
                  <MenuItem value="">
                    <em>{t("Select a pond...")}</em>
                  </MenuItem>
                  {ponds.map((pond) => (
                    <MenuItem key={pond.id} value={pond.id.toString()}>
                      {pond.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={t("Maintenance Type")}
                value={templateForm.maintenanceType}
                onChange={(e) => setTemplateForm({...templateForm, maintenanceType: e.target.value})}
                margin="normal"
              />
              <TextField
                fullWidth
                label={t("Due Date")}
                type="date"
                value={templateForm.dueDate}
                onChange={(e) => setTemplateForm({...templateForm, dueDate: e.target.value})}
                margin="normal"
                InputLabelProps={{shrink: true}}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>{t("Cancel")}</Button>
          <Button onClick={handleCreateTemplateNotification} variant="contained" disabled={!selectedTemplate}>
            {t("Create")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationCenter;
